import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Tag } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBrands() {
  const { getAuthHeader } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/brands`, { headers: getAuthHeader() });
      setBrands(res.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Erè pandan chaje brand');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingBrand) {
        await axios.put(`${API}/brands/${editingBrand.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Brand mete ajou!');
      } else {
        await axios.post(`${API}/brands`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Brand kreye!');
      }

      setIsDialogOpen(false);
      setEditingBrand(null);
      resetForm();
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      toast.error('Erè pandan sove brand');
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      description: brand.description || '',
      logo_url: brand.logo_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(`${API}/brands/${deleteId}`, {
        headers: getAuthHeader()
      });
      toast.success('Brand efase!');
      fetchBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Erè pandan efase brand');
    } finally {
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo_url: ''
    });
  };

  const openNewDialog = () => {
    setEditingBrand(null);
    resetForm();
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#001A3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-brands">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#001A3E]">Brand</h1>
          <p className="text-gray-500">{brands.length} brand total</p>
        </div>
        <Button className="bg-[#001A3E] hover:bg-[#001A3E]/90" onClick={openNewDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Ajoute Brand
        </Button>
      </div>

      {/* Brands Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {brands.map((brand) => (
          <Card key={brand.id} className="overflow-hidden">
            <div className="h-24 bg-gray-100 flex items-center justify-center p-4">
              {brand.logo_url ? (
                <img
                  src={brand.logo_url}
                  alt={brand.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <Tag className="h-10 w-10 text-[#001A3E]/30" />
              )}
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-[#001A3E]">{brand.name}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {brand.description || 'Pa gen deskripsyon'}
              </p>
              <div className="flex items-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEdit(brand)}
                >
                  <Pencil className="mr-1 h-3 w-3" />
                  Modifye
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteId(brand.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {brands.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            Pa gen brand. Klike sou "Ajoute Brand" pou kòmanse.
          </CardContent>
        </Card>
      )}

      {/* Brand Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBrand ? 'Modifye Brand' : 'Nouvo Brand'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Non *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Egzanp: Apple"
              />
            </div>
            <div>
              <Label htmlFor="description">Deskripsyon</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Dekri brand la..."
              />
            </div>
            <div>
              <Label htmlFor="logo_url">URL Logo</Label>
              <Input
                id="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            {formData.logo_url && (
              <div className="flex justify-center p-4 bg-gray-50 rounded">
                <img
                  src={formData.logo_url}
                  alt="Preview"
                  className="max-h-20 object-contain"
                />
              </div>
            )}
            <Button type="submit" className="w-full bg-[#001A3E] hover:bg-[#001A3E]/90">
              {editingBrand ? 'Mete Ajou' : 'Kreye'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Efase Brand?</AlertDialogTitle>
            <AlertDialogDescription>
              Aksyon sa a pa ka defè. Brand la pral efase pèmanantman.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anile</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Efase
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
